import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FileText, CheckCircle2, Eye, Send, ThumbsUp, ThumbsDown, Play,
  Filter, Globe
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { formatDate, statusBadgeClass, statusLabel } from '@/lib/utils';
import { toast } from 'sonner';
import api from '@/lib/api';



const WORKFLOW_LABELS = {
  draft: { label: 'Taslak', color: 'badge-taslak' },
  in_review: { label: 'Incelemede', color: 'badge-duraklatildi' },
  approved: { label: 'Onaylandi', color: 'badge-aktif' },
  published: { label: 'Yayinda', color: 'badge-aktif' },
  archived: { label: 'Arsiv', color: 'badge-taslak' },
};

export default function ReviewQueuePage() {
  const [pages, setPages] = useState([]);
  const [sites, setSites] = useState([]);
  const [siteFilter, setSiteFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (siteFilter !== 'all') params.site_id = siteFilter;
      const [queueRes, sitesRes] = await Promise.all([
        api.get('/review-queue', { params }),
        api.get('/sites'),
      ]);
      let items = queueRes.data;
      if (statusFilter !== 'all') {
        items = items.filter(p => (p.workflow_status || p.status) === statusFilter);
      }
      setPages(items);
      setSites(sitesRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [siteFilter, statusFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleWorkflow = async (siteId, pageId, action) => {
    try {
      const { data } = await api.put(`/sites/${siteId}/pages/${pageId}/workflow`, { action });
      toast.success(data.message);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Islem basarisiz');
    }
  };

  const getSiteName = (siteId) => sites.find(s => s.id === siteId)?.brand_name || '';

  const counts = {
    draft: pages.filter(p => (p.workflow_status || p.status) === 'draft').length,
    in_review: pages.filter(p => (p.workflow_status || p.status) === 'in_review').length,
    approved: pages.filter(p => (p.workflow_status || p.status) === 'approved').length,
  };

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight font-['Space_Grotesk']">Icerik Inceleme Kuyrugu</h1>
          <p className="text-sm text-muted-foreground mt-1">{pages.length} sayfa bekliyor</p>
        </div>
        <div className="flex gap-2">
          <Select value={siteFilter} onValueChange={setSiteFilter}>
            <SelectTrigger className="w-[200px] bg-secondary/40 border-border/70"><SelectValue placeholder="Site" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tum Siteler</SelectItem>
              {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.brand_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px] bg-secondary/40 border-border/70"><SelectValue placeholder="Durum" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tum Durumlar</SelectItem>
              <SelectItem value="draft">Taslak</SelectItem>
              <SelectItem value="in_review">Incelemede</SelectItem>
              <SelectItem value="approved">Onaylandi</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </motion.div>

      {/* Counts */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-border/70"><CardContent className="pt-5"><p className="text-xs text-muted-foreground uppercase">Taslak</p><p className="text-2xl font-semibold mt-1">{counts.draft}</p></CardContent></Card>
        <Card className="border-border/70"><CardContent className="pt-5"><p className="text-xs text-muted-foreground uppercase">Incelemede</p><p className="text-2xl font-semibold mt-1 text-amber-400">{counts.in_review}</p></CardContent></Card>
        <Card className="border-border/70"><CardContent className="pt-5"><p className="text-xs text-muted-foreground uppercase">Onaylandi</p><p className="text-2xl font-semibold mt-1 text-emerald-400">{counts.approved}</p></CardContent></Card>
      </div>

      {/* Queue */}
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 rounded-xl shimmer" />)}</div>
      ) : pages.length === 0 ? (
        <Card className="border-border/70"><CardContent className="py-12 text-center"><FileText className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" /><p className="text-sm text-muted-foreground">Kuyrukta sayfa yok</p></CardContent></Card>
      ) : (
        <div className="space-y-2">
          {pages.map(page => {
            const ws = page.workflow_status || page.status || 'draft';
            const wl = WORKFLOW_LABELS[ws] || WORKFLOW_LABELS.draft;
            return (
              <Card key={page.id} className="border-border/70">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 min-w-0">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm text-[hsl(var(--primary))]">{page.slug}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${wl.color}`}>{wl.label}</span>
                          {page.language && page.language !== 'tr' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-400/10 text-blue-400 uppercase">{page.language}</span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[400px]">{page.seo_title || 'Baslik yok'}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{getSiteName(page.site_id)} - {formatDate(page.updated_at)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {ws === 'draft' && (
                        <Button size="sm" variant="outline" className="text-xs gap-1 border-border/70" onClick={() => handleWorkflow(page.site_id, page.id, 'submit_review')}>
                          <Send className="w-3 h-3" /> Incelemeye Gonder
                        </Button>
                      )}
                      {ws === 'in_review' && (
                        <>
                          <Button size="sm" className="text-xs gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => handleWorkflow(page.site_id, page.id, 'approve')}>
                            <ThumbsUp className="w-3 h-3" /> Onayla
                          </Button>
                          <Button size="sm" variant="outline" className="text-xs gap-1 border-red-400/30 text-red-400" onClick={() => handleWorkflow(page.site_id, page.id, 'reject')}>
                            <ThumbsDown className="w-3 h-3" /> Reddet
                          </Button>
                        </>
                      )}
                      {ws === 'approved' && (
                        <Button size="sm" className="text-xs gap-1 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" onClick={() => handleWorkflow(page.site_id, page.id, 'publish')}>
                          <Play className="w-3 h-3" /> Yayinla
                        </Button>
                      )}
                      <Link to={`/preview/${page.site_id}?slug=${encodeURIComponent(page.slug)}`}>
                        <Button size="sm" variant="ghost" className="text-xs"><Eye className="w-3.5 h-3.5" /></Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
